import type { DemandHistoryPoint, ForecastPoint } from "@/api/demandForecast";

import { gaussianSmooth, smooth } from "./smoothing";
import type { Metric, Point } from "./types";

const Z = 1.65; // z=1.65 → ~95% one-sided (≈90% two-sided) band

// ── Internal helpers ──────────────────────────────────────────────────────────

/** Daily values for `metric` (the server already applied the raw/cleansed mode). */
function dailyValues(buckets: DemandHistoryPoint[], metric: Metric): number[] {
  return buckets.map((b) => b[metric]);
}

/** Re-pair a transformed numeric series back onto its source dates. */
function pairWithDates(source: { date: string }[], values: number[]): Point[] {
  return source.map((s, i) => ({ x: s.date, y: values[i] }));
}

// ── Daily series (raw, paired with dates) ─────────────────────────────────────

/** Daily series for a metric — raw source for range totals and the nav sparkline. */
export function metricPoints(
  buckets: DemandHistoryPoint[],
  metric: Metric,
): Point[] {
  return pairWithDates(buckets, dailyValues(buckets, metric));
}

/** Daily forecast mean paired with forecast dates (raw — used for the nav forecast tail). */
export function forecastMeanDaily(forecast: ForecastPoint[]): Point[] {
  return forecast.map((f) => ({ x: f.date, y: f.mean }));
}

// ── Period-scaled series builders ─────────────────────────────────────────────

/**
 * The rough "demand per period" baseline. The daily series is passed through a
 * centered period-window mean (`smooth`) and multiplied by `periodDays`, so it
 * reads as a rolling sum over one period (week / month / quarter).
 */
export function baselinePoints(
  buckets: DemandHistoryPoint[],
  metric: Metric,
  periodDays: number,
): Point[] {
  const daily = dailyValues(buckets, metric);
  const perPeriod = smooth(daily, periodDays).map((v) => v * periodDays);
  return pairWithDates(buckets, perPeriod);
}

/**
 * The smooth trend line. The daily series is passed through a wider
 * Gaussian-weighted smoothing (edge-aware window) and converted to
 * demand-per-period (× `periodDays`).
 */
export function trendPoints(
  buckets: DemandHistoryPoint[],
  metric: Metric,
  periodDays: number,
  windowDays: number,
): Point[] {
  const daily = dailyValues(buckets, metric);
  const perPeriod = gaussianSmooth(daily, windowDays).map(
    (v) => v * periodDays,
  );
  return pairWithDates(buckets, perPeriod);
}

/** Forecast mean line in demand-per-period units (smoothed × `periodDays`). */
export function forecastMeanPoints(
  forecast: ForecastPoint[],
  periodDays: number,
): Point[] {
  const means = forecast.map((f) => f.mean);
  const perPeriod = smooth(means, periodDays).map((v) => v * periodDays);
  return pairWithDates(forecast, perPeriod);
}

/**
 * Forecast confidence band at ±1.65σ (≈90% interval), in demand-per-period
 * units. The variance of a period-sum of independent daily values adds, so the
 * per-period variance is the smoothed daily variance × `periodDays`; σ is its
 * square root. Upper and lower share the same x values; lower is clamped at 0.
 */
export function forecastBandPoints(
  forecast: ForecastPoint[],
  periodDays: number,
): { upper: Point[]; lower: Point[] } {
  const means = forecast.map((f) => f.mean);
  const variances = forecast.map((f) => f.variance);
  const perMean = smooth(means, periodDays).map((v) => v * periodDays);
  const perVar = smooth(variances, periodDays).map((v) => v * periodDays);
  const upper: Point[] = [];
  const lower: Point[] = [];
  forecast.forEach((f, i) => {
    const sigma = Math.sqrt(perVar[i]);
    upper.push({ x: f.date, y: perMean[i] + Z * sigma });
    lower.push({ x: f.date, y: Math.max(0, perMean[i] - Z * sigma) });
  });
  return { upper, lower };
}
